import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Chip,
  IconButton,
  Tooltip,
  Button,
  LinearProgress,
  Stack,
  Collapse,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Remove,
  Delete,
  Refresh,
  Add,
  CameraAlt,
  Analytics,
  BarChart,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

const KeywordSidebar = ({
  keywords = [],
  selectedKeyword,
  onKeywordSelect,
  onKeywordDelete,
  onKeywordRefresh,
  onAddKeyword,
  onCreateMeasurement,
  onCalculateLocalVolume,
  loading = false,
  overview = null,
  locationId = null,
  calculatingVolume = {}
}) => {
  const [expandedKeyword, setExpandedKeyword] = useState(null);

  const getRankColor = (rank) => {
    if (!rank || rank === 'N/A') return '#9e9e9e';
    if (rank <= 3) return '#4caf50';
    if (rank <= 10) return '#ff9800';
    return '#f44336';
  };

  const getRankStatus = (rank) => {
    if (!rank || rank === 'N/A') return { text: 'Nicht gefunden', color: '#9e9e9e', icon: '❓' };
    if (rank <= 3) return { text: 'Top', color: '#4caf50', icon: '🏆' };
    if (rank <= 10) return { text: 'Gut', color: '#ff9800', icon: '👍' };
    return { text: 'Schwach', color: '#f44336', icon: '📈' };
  };

  const getTrendIcon = (trend) => {
    if (!trend || trend === 0) return <Remove sx={{ color: '#9e9e9e', fontSize: '1rem' }} />;
    if (trend > 0) return <TrendingUp sx={{ color: '#4caf50', fontSize: '1rem' }} />;
    return <TrendingDown sx={{ color: '#f44336', fontSize: '1rem' }} />;
  };

  const getTrendText = (trend) => {
    if (!trend || trend === 0) return '';
    if (trend > 0) return `+${trend}`;
    return `${trend}`;
  };

  // Berechne Trend aus historischen Daten (aktienmäßiger Indikator)
  const calculateRankingTrend = (keyword) => {
    // Simuliere historische Daten oder nutze echte wenn verfügbar
    if (!keyword.rankingHistory || keyword.rankingHistory.length < 2) {
      // Fallback: berechne Trend aus aktuellen Metriken
      const current = keyword.averageRank || 0;
      const best = keyword.bestRank || current;
      
      if (current > 0 && best > 0) {
        const improvement = best - current;
        if (improvement > 2) return 'strong-up';
        if (improvement > 0) return 'up';
        if (improvement < -2) return 'strong-down';
        if (improvement < 0) return 'down';
      }
      return 'neutral';
    }
    
    // Analysiere die letzten 3-5 Datenpunkte für Trend
    const recentHistory = keyword.rankingHistory.slice(-5);
    if (recentHistory.length < 2) return 'neutral';
    
    let improvementCount = 0;
    let declineCount = 0;
    
    for (let i = 1; i < recentHistory.length; i++) {
      const prev = recentHistory[i-1].averageRank;
      const curr = recentHistory[i].averageRank;
      
      if (curr < prev) improvementCount++; // Niedrigerer Rang = besser
      else if (curr > prev) declineCount++;
    }
    
    const trendRatio = improvementCount / (recentHistory.length - 1);
    
    if (trendRatio >= 0.8) return 'strong-up';
    if (trendRatio >= 0.6) return 'up';
    if (trendRatio <= 0.2) return 'strong-down';
    if (trendRatio <= 0.4) return 'down';
    return 'neutral';
  };

  const getTrendIndicator = (keyword) => {
    const trend = calculateRankingTrend(keyword);
    
    const indicators = {
      'strong-up': { 
        icon: <TrendingUp sx={{ fontSize: '1rem', color: '#2e7d32' }} />, 
        color: '#2e7d32',
        tooltip: 'Starker Aufwärtstrend',
        text: '📈'
      },
      'up': { 
        icon: <TrendingUp sx={{ fontSize: '1rem', color: '#4caf50' }} />, 
        color: '#4caf50',
        tooltip: 'Aufwärtstrend',
        text: '↗️'
      },
      'neutral': { 
        icon: <Remove sx={{ fontSize: '1rem', color: '#9e9e9e' }} />, 
        color: '#9e9e9e',
        tooltip: 'Stabil',
        text: '➡️'
      },
      'down': { 
        icon: <TrendingDown sx={{ fontSize: '1rem', color: '#f44336' }} />, 
        color: '#f44336',
        tooltip: 'Abwärtstrend',
        text: '↘️'
      },
      'strong-down': { 
        icon: <TrendingDown sx={{ fontSize: '1rem', color: '#c62828' }} />, 
        color: '#c62828',
        tooltip: 'Starker Abwärtstrend',
        text: '📉'
      }
    };
    
    return indicators[trend] || indicators.neutral;
  };

  const getLastAnalyzedText = (lastAnalyzed) => {
    if (!lastAnalyzed) return 'nie';

    let dateValue = lastAnalyzed;

    // Handle MongoDB Extended JSON format: {$date: '2026-08-03T08:09:50.994Z'}
    if (typeof lastAnalyzed === 'object' && lastAnalyzed !== null && lastAnalyzed.$date) {
      dateValue = lastAnalyzed.$date;
      console.log('✅ Extracted MongoDB Extended JSON date:', dateValue);
    }

    let analyzed = new Date(dateValue);

    // Debug log to understand format
    if (isNaN(analyzed.getTime())) {
      console.log('⚠️ Invalid date format:', { lastAnalyzed, type: typeof lastAnalyzed });
    }

    // If date parsing failed, try parsing German date format (DD.MM.YYYY)
    if (isNaN(analyzed.getTime()) && typeof dateValue === 'string') {
      const germanDateMatch = dateValue.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
      if (germanDateMatch) {
        const [, day, month, year] = germanDateMatch;
        analyzed = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        console.log('✅ Parsed German date format:', { input: dateValue, parsed: analyzed.toISOString() });
      }
    }

    if (isNaN(analyzed.getTime())) {
      console.log('❌ Could not parse date:', dateValue);
      return 'nie';
    }

    const now = new Date();
    const diffInHours = Math.floor((now - analyzed) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'jetzt';
    if (diffInHours < 24) return `${diffInHours}h`;

    const diffInDays = Math.floor(diffInHours / 24);
    return isNaN(diffInDays) ? 'nie' : `${diffInDays}d`;
  };

  // Render detailed local volume information
  const renderLocalVolumeDetails = (keyword) => {
    if (!keyword.localVolume) return null;

    let calculations = [];
    if (Array.isArray(keyword.localVolume)) {
      calculations = keyword.localVolume;
    } else if (keyword.localVolume.localVolume) {
      calculations = [keyword.localVolume];
    }

    if (calculations.length === 0) return null;

    return (
      <Box sx={{ pl: 4, pr: 2, py: 1.5, backgroundColor: '#f5f5f5', borderLeft: '3px solid #4caf50' }}>
        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2e7d32', display: 'block', mb: 1 }}>
           Lokales Suchvolumen
        </Typography>

        {calculations.map((calc, idx) => (
          <Box key={idx} sx={{ mb: idx < calculations.length - 1 ? 1.5 : 0 }}>
            {calculations.length > 1 && (
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Berechnung #{idx + 1}
              </Typography>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1 }}>
              <Box sx={{ backgroundColor: '#c8e6c9', p: 1, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#2e7d32', fontWeight: 700, textTransform: 'uppercase' }}>
                  📍 Regional
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1b5e20', mt: 0.3 }}>
                  {calc.localVolume?.toLocaleString?.('de-DE') || '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#2e7d32' }}>
                  Suchvorgänge/Monat
                </Typography>
              </Box>

              <Box sx={{ backgroundColor: '#bbdefb', p: 1, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#1565c0', fontWeight: 700, textTransform: 'uppercase' }}>
                  🌍 {calc.country || 'N/A'}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0d47a1', mt: 0.3 }}>
                  {calc.nationalVolume?.toLocaleString?.('de-DE') || '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#1565c0' }}>
                  Suchvorgänge/Monat
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 600 }}>
                  👥 BEVÖLKERUNG (15km)
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.primary' }}>
                  {calc.localPopulation?.toLocaleString('de-DE')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 600 }}>
                  % ANTEIL
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#ff6f00' }}>
                  {(calc.populationRatio * 100).toFixed(2)}%
                </Typography>
              </Box>
            </Box>

            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              {new Date(calc.calculatedAt).toLocaleString('de-DE')}
            </Typography>

            {idx < calculations.length - 1 && <Divider sx={{ my: 1 }} />}
          </Box>
        ))}
      </Box>
    );
  };


  // Ultra-kompakte Keyword-Zeile
  const renderCompactKeywordItem = (keyword) => {
    const isSelected = selectedKeyword === keyword.keyword;
    const rankStatus = getRankStatus(keyword.averageRank);
    const trend = keyword.trend || 0;

    // Debug: Log ALL keyword data
    console.log(`🔍 RENDER Keyword: "${keyword.keyword}"`, {
      hasLocalVolume: !!keyword.localVolume,
      localVolumeValue: keyword.localVolume,
      isArray: Array.isArray(keyword.localVolume),
      length: Array.isArray(keyword.localVolume) ? keyword.localVolume.length : 'N/A'
    });
    
    const isExpanded = expandedKeyword === keyword.keyword;
    const hasLocalVolume = keyword.localVolume && ((Array.isArray(keyword.localVolume) && keyword.localVolume.length > 0) || keyword.localVolume.localVolume);

    return (
      <Box key={keyword.keyword} sx={{ mb: 0.75 }}>
        <ListItem disablePadding>
          <ListItemButton
            selected={isSelected}
            onClick={() => onKeywordSelect(keyword.keyword)}
            sx={{
              py: 1,
              px: 1.5,
              borderRadius: 1,
              minHeight: 'auto',
              border: '1px solid #e0e0e0',
              backgroundColor: isSelected ? '#e3f2fd' : 'white',
              '&.Mui-selected': {
                backgroundColor: '#e3f2fd',
                borderColor: '#1976d2',
                borderWidth: '2px'
              },
              '&:hover': {
                backgroundColor: isSelected ? '#e3f2fd' : '#f8f9fa',
                borderColor: isSelected ? '#1976d2' : '#bdbdbd',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
          {/* Main content - Icon + Keyword + Actions */}
          <Box display="flex" alignItems="flex-start" flex={1} minWidth={0} flexDirection="column" gap={0.75}>
            {/* First row - Icon + Keyword + Actions */}
            <Box display="flex" alignItems="center" flex={1} minWidth={0} width="100%">
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: rankStatus.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5,
                  flexShrink: 0,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }}
              >
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'white' }}>
                  {keyword.averageRank ? Math.round(keyword.averageRank) : '?'}
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  fontSize: '1rem',
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? '#1976d2' : 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1
                }}
              >
                {keyword.keyword}
              </Typography>

              {/* Trend + Last Analyzed + Actions */}
              <Box display="flex" alignItems="center" gap={0.5} ml={1} flexShrink={0}>
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    p: 0.25,
                    borderRadius: 0.5,
                    backgroundColor: `${getTrendIndicator(keyword).color}15`,
                    border: `1px solid ${getTrendIndicator(keyword).color}30`
                  }}
                  title={getTrendIndicator(keyword).tooltip}
                >
                  {getTrendIndicator(keyword).icon}
                </Box>

                {trend !== 0 && (
                  <Box display="flex" alignItems="center">
                    {getTrendIcon(trend)}
                    <Typography sx={{ fontSize: '0.7rem', color: trend > 0 ? '#4caf50' : '#f44336' }}>
                      {getTrendText(trend)}
                    </Typography>
                  </Box>
                )}

                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {getLastAnalyzedText(keyword.lastAnalyzed)}
                </Typography>

                {/* Actions */}
                <Box display="flex" gap={0.5}>
                  {hasLocalVolume && (
                    <Tooltip title={isExpanded ? 'Details ausblenden' : 'Details anzeigen'}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedKeyword(isExpanded ? null : keyword.keyword);
                        }}
                        sx={{
                          p: 0.5,
                          backgroundColor: 'rgba(76, 175, 80, 0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(76, 175, 80, 0.12)'
                          }
                        }}
                      >
                        {isExpanded ? (
                          <ExpandLess sx={{ fontSize: '1rem', color: '#4caf50' }} />
                        ) : (
                          <ExpandMore sx={{ fontSize: '1rem', color: '#4caf50' }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title={keyword.localVolume ? `Local Volume: ${Array.isArray(keyword.localVolume) ? keyword.localVolume[keyword.localVolume.length - 1]?.localVolume || '—' : keyword.localVolume?.localVolume || '—'}` : 'Calculate Local Volume'}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onCalculateLocalVolume) {
                          onCalculateLocalVolume(keyword.keyword);
                        }
                      }}
                      disabled={calculatingVolume?.[keyword.keyword] || false}
                      sx={{
                        p: 0.5,
                        backgroundColor: keyword.localVolume ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 152, 0, 0.08)',
                        '&:hover': {
                          backgroundColor: keyword.localVolume ? 'rgba(76, 175, 80, 0.12)' : 'rgba(255, 152, 0, 0.12)'
                        }
                      }}
                    >
                      <BarChart sx={{
                        fontSize: '1rem',
                        color: keyword.localVolume ? '#4caf50' : '#ff9800'
                      }} />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onKeywordRefresh(keyword.keyword);
                    }}
                    disabled={loading}
                    sx={{
                      p: 0.5,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.12)'
                      }
                    }}
                  >
                    <Refresh sx={{ fontSize: '1rem', color: '#1976d2' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onKeywordDelete(keyword.keyword);
                    }}
                    sx={{
                      p: 0.5,
                      backgroundColor: 'rgba(244, 67, 54, 0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.12)'
                      }
                    }}
                  >
                    <Delete sx={{ fontSize: '1rem', color: '#f44336' }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            {/* Second row - Local Volume Badges */}
            {keyword.localVolume && (
              (() => {
                // Get latest calculation (support both array and single object for backwards compatibility)
                let latestVolume = null;
                if (Array.isArray(keyword.localVolume) && keyword.localVolume.length > 0) {
                  latestVolume = keyword.localVolume[keyword.localVolume.length - 1];
                } else if (keyword.localVolume.localVolume) {
                  latestVolume = keyword.localVolume;
                }

                return latestVolume && latestVolume.localVolume && latestVolume.nationalVolume ? (
                  <Box display="flex" gap={0.5} alignItems="center" width="100%">
                    <Chip
                      label={`${latestVolume.localVolume?.toLocaleString('de-DE') || '—'}/monat 📍`}
                      size="small"
                      title={`Lokales Volumen: ${latestVolume.localVolume?.toLocaleString('de-DE') || 'N/A'}/monat`}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: '#c8e6c9',
                        color: '#2e7d32',
                        fontWeight: 600,
                        flexShrink: 0,
                        '& .MuiChip-label': {
                          padding: '0 6px'
                        }
                      }}
                    />
                    <Chip
                      label={`${latestVolume.nationalVolume?.toLocaleString('de-DE') || '—'}/monat 🇩🇪`}
                      size="small"
                      title={`Nationales Volumen in ${latestVolume.country || 'N/A'}`}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: '#bbdefb',
                        color: '#1565c0',
                        fontWeight: 600,
                        flexShrink: 0,
                        '& .MuiChip-label': {
                          padding: '0 6px'
                        }
                      }}
                    />
                  </Box>
                ) : null;
              })()
            )}
          </Box>
        </ListItemButton>
        </ListItem>

        {/* Local Volume Details - Collapsible */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {renderLocalVolumeDetails(keyword)}
        </Collapse>
      </Box>
    );
  };


  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Minimaler Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1rem', color: 'text.primary' }}>
          Keywords
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {keywords.length} gespeichert
        </Typography>
      </Box>

      {/* Keywords Liste */}
      <Box sx={{ flex: 1, overflowY: 'auto', mb: 1 }}>
        {keywords.length === 0 ? (
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            px: 2,
            color: 'text.secondary'
          }}>
            <Analytics sx={{ fontSize: 40, mb: 2, opacity: 0.5 }} />
            <Typography variant="body2" sx={{ fontSize: '0.85rem', mb: 2, textAlign: 'center' }}>
              Noch keine Keywords analysiert
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', mb: 3, textAlign: 'center' }}>
              Starten Sie mit Ihrer ersten Keyword-Analyse
            </Typography>
            
            {/* Enterprise-Grade Add Keyword Button - Properly Centered */}
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={onAddKeyword}
              sx={{
                mt: 2,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                backgroundColor: 'primary.main',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'translateY(0px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Erstes Keyword hinzufügen
            </Button>
          </Box>
        ) : (
          <List disablePadding>
            {keywords.map(renderCompactKeywordItem)}
            
            {/* Elegante Add-Zeile zwischen Keywords */}
            <ListItem disablePadding sx={{ mb: 0.75 }}>
              <ListItemButton
                onClick={onAddKeyword}
                sx={{ 
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  minHeight: 'auto',
                  border: '1px dashed #d0d0d0',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                    borderColor: '#1976d2',
                    borderStyle: 'solid'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                  <Add sx={{ 
                    fontSize: '1.2rem', 
                    color: 'text.secondary',
                    mr: 1,
                    '&:hover': {
                      color: '#1976d2'
                    }
                  }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.85rem',
                      color: 'text.secondary',
                      fontWeight: 500,
                      '&:hover': {
                        color: '#1976d2'
                      }
                    }}
                  >
                    Neues Keyword hinzufügen
                  </Typography>
                </Box>
              </ListItemButton>
            </ListItem>
          </List>
        )}
      </Box>

      {/* Elegante Action-Buttons unten */}
      <Box sx={{ 
        borderTop: '1px solid #f0f0f0',
        pt: 1.5,
        mt: 'auto'
      }}>
        <Stack direction="row" spacing={1}>
          {/* Add Keyword Button */}
          <Button
            onClick={onAddKeyword}
            variant="text"
            startIcon={<Add />}
            sx={{
              flex: 1,
              color: 'text.secondary',
              backgroundColor: 'transparent',
              border: '1px dashed #d0d0d0',
              py: 1,
              fontSize: '0.8rem',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#f8f9fa',
                borderColor: '#1976d2',
                color: '#1976d2'
              }
            }}
          >
            Keyword hinzufügen
          </Button>
          
          {/* Measurement Button */}
          <Tooltip title="Neue Messung für alle Keywords">
            <IconButton
              onClick={onCreateMeasurement}
              disabled={loading}
              sx={{
                color: 'text.secondary',
                border: '1px dashed #d0d0d0',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                  borderColor: '#1976d2',
                  color: '#1976d2'
                }
              }}
            >
              <CameraAlt />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
};

export default KeywordSidebar;